/**
 * useSubscriptionPolling Hook - Event-driven subscription data refresh
 *
 * Fetches grace period and trial status on mount and when the user returns
 * to the tab (visibilitychange). Runs for ALL authenticated users so that
 * every user in the org sees accurate subscription banners.
 */

import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/stateHooks';
import {
  fetchMyGracePeriodStatus,
  fetchMyTrialStatus,
} from '../redux/slices/subscriptionSliceModel';

/**
 * Centralized subscription polling hook
 * Fetches all subscription data on mount and sets up refresh interval.
 * SECURITY: Only polls if user has BILLING_MANAGEMENT privilege
 * ETL: Skip entirely during initial system setup — no subscription exists yet
 */
export const useSubscriptionPolling = (): void => {
  const dispatch = useAppDispatch();
  const setupComplete = useAppSelector((state) => state.etl.setupComplete);

  useEffect(() => {
    const refresh = () => {
      dispatch(fetchMyGracePeriodStatus());
      dispatch(fetchMyTrialStatus());
    };

    // Fetch on mount
    refresh();

    // Refetch when user returns to this tab
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('visibilitychange', onVisibility);
    return () => window.removeEventListener('visibilitychange', onVisibility);
  }, [dispatch, setupComplete]);
};
